import sanityClient, { sanityWriteClient } from "../CMS/client";
import imageUrlBuilder from "@sanity/image-url";
import { Link } from "react-router-dom";
import { FaMapMarkerAlt } from "react-icons/fa";
import { MdEmail } from "react-icons/md";
import { ImPhone } from "react-icons/im";
import { AiFillClockCircle } from "react-icons/ai";
import { BsFacebook } from "react-icons/bs";
import { RiInstagramFill } from "react-icons/ri";
import { FaTiktok } from "react-icons/fa";
import { BsTwitter } from "react-icons/bs";
import { BsYoutube } from "react-icons/bs";
import { BiSolidShoppingBag } from "react-icons/bi";

const urlFor = imageUrlBuilder(sanityClient);

/**
 * Image size presets for different use cases.
 * Using appropriate sizes significantly reduces bandwidth.
 */
export const IMAGE_SIZES = {
  thumbnail: 200,      // Small thumbnails, cart items
  card: 400,           // Product cards, blog cards
  cardLarge: 600,      // Featured product cards
  carousel: 800,       // Carousel images
  productDetail: 1000, // Product detail page
  full: 1600,          // Full-width images
};

/**
 * Generates an optimized image URL from a Sanity image reference.
 * @param {string} ref - The Sanity image asset reference
 * @param {number} width - Target width in pixels (default: 800)
 * @param {number} quality - Image quality 0-100 (default: 80)
 * @returns {string} Optimized image URL with proper format and size
 */
export const getImageSource = (ref, width = IMAGE_SIZES.carousel, quality = 80) => {
  return urlFor
    .image(ref)
    .auto("format")
    .width(width)
    .quality(quality)
    .fit("max")
    .toString();
};

/**
 * Generates optimized image URL parameters for direct URL usage.
 * Use this when you have a raw Sanity asset URL and need to add optimization params.
 * @param {string} url - The raw Sanity asset URL
 * @param {number} width - Target width in pixels
 * @param {number} quality - Image quality 0-100 (default: 80)
 * @returns {string} URL with optimization parameters
 */
export const optimizeImageUrl = (url, width = IMAGE_SIZES.card, quality = 80) => {
  if (!url) return "";
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}w=${width}&q=${quality}&fit=max&auto=format`;
};

/**
 * Fetches data from Sanity using the read-only CDN client.
 * @param {string} request - GROQ query string
 * @returns {Promise<any>} Query results
 */
export async function fetchData(request) {
  try {
    const data = await sanityClient.fetch(request);
    return data;
  } catch (error) {
    console.error(error);
    return null;
  }
}

/**
 * Updates the product quantity in Sanity CMS.
 * Uses the write client (no CDN) for mutations.
 * @param {string} productId - The ID of the product to update.
 * @param {number} quantity - The quantity to decrement.
 * @returns {Promise<Object>} The result of the patch operation.
 * @throws {Error} If the update fails.
 */
export async function patchData(productId, quantity) {
  return sanityWriteClient
    .patch(productId)
    .dec({ quantity: quantity })
    .commit();
}

export const createSocialMediaArray = (socialMediaFromSettings) => {
  if (socialMediaFromSettings) {
    return Object.entries(socialMediaFromSettings).map(([name, url]) => ({
      name,
      url,
      type: "social",
    }));
  } else {
    return [];
  }
};

export const createNavItemsArray = async (navigation) => {
  const createSubmenuItems = (subMenuItems) => {
    return subMenuItems.map((subItem) => ({
      name: subItem.title,
      url: `/${subItem.slug}`,
      type: "page",
    }));
  };

  const navItemsArray = navigation
    ? navigation.map((navItem) => {
        const item = {
          name: navItem.item.title,
          url: `/${navItem.item.slug}`,
          type: "page",
          submenu: navItem.subMenuItems ? createSubmenuItems(navItem.subMenuItems) : [],
        };

        return item;
      })
    : [];

  return navItemsArray;
};

export const createContactsArray = (contactsFromSettings) => {
  return contactsFromSettings
    ? Object.entries(contactsFromSettings).map(([icon, text]) => ({
        icon,
        text,
      }))
    : [];
};

export function setSiteName(name) {
  document.title = name;
}

export function dimColor(hexColor, dimPercentage) {
  // Remove the '#' symbol if present
  if (hexColor !== undefined) {
    hexColor = hexColor.replace("#", "");
    // Convert the hex color to RGB
    const red = parseInt(hexColor.substring(0, 2), 16);
    const green = parseInt(hexColor.substring(2, 4), 16);
    const blue = parseInt(hexColor.substring(4, 6), 16);

    // Calculate the dimmed RGB values
    const dimFactor = 1 - dimPercentage / 100;
    const dimmedRed = Math.floor(red * dimFactor);
    const dimmedGreen = Math.floor(green * dimFactor);
    const dimmedBlue = Math.floor(blue * dimFactor);

    // Convert the dimmed RGB values back to hex
    const dimmedHex = `#${dimmedRed.toString(16).padStart(2, "0")}${dimmedGreen
      .toString(16)
      .padStart(2, "0")}${dimmedBlue.toString(16).padStart(2, "0")}`;

    return dimmedHex;
  }
  return;
}

export function mapContacts(contactsArray, iconColor) {
  return contactsArray.map((contact) => {
    let url = undefined;
    if (contact.icon === "phone") {
      url = `tel:${contact.text}`;
    } else if (contact.icon === "email") {
      url = `mailto:${contact.text}`;
    } else if (contact.icon === "shop") {
      url = `https://${contact.text}`;
    }

    const text = (url) => {
      if (url !== undefined) {
        return (
          <a href={url} target="_blank" rel="noreferrer">
            {contact.text}
          </a>
        );
      } else {
        return <p>{contact.text}</p>;
      }
    };

    return (
      <div className="contact-holder" key={contact.text}>
        {setIcon(22, contact.icon, iconColor)}
        {text(url)}
      </div>
    );
  });
}

/**
 * Maps menu items to Link components for footer navigation.
 * Uses React Router Link for client-side navigation.
 * @param {Array} menuItemsArray - Array of menu items
 * @returns {Array} Array of Link elements
 */
export function mapMenu(menuItemsArray) {
  return menuItemsArray.map((menuItem, index) => {
    if (menuItem.type === "page") {
      return (
        <Link to={menuItem.url} key={`menu-item-${index}`}>
          <p className="normal-paragraph">{menuItem.name}</p>
        </Link>
      );
    } else {
      return null;
    }
  });
}

export function setIcon(size, type, color) {
  const types = {
    address: FaMapMarkerAlt,
    email: MdEmail,
    phone: ImPhone,
    workTime: AiFillClockCircle,
    shop: BiSolidShoppingBag,
    facebook: BsFacebook,
    instagram: RiInstagramFill,
    tiktok: FaTiktok,
    twitter: BsTwitter,
    youtube: BsYoutube,
  };

  const Icon = types[type];
  const socialMedias = ["facebook", "instagram", "tiktok", "twitter", "youtube"];

  if (socialMedias.some((media) => media === type)) {
    return <Icon color={color} className="link-to-social-media" size={size} />;
  } else {
    return <Icon className="size-[22px]" fill={color} />;
  }
}

/**
 * Checks the current quantity of a product in Sanity.
 * Uses the write client to ensure fresh data (bypasses CDN cache).
 * @param {string} productId - The product ID to check
 * @returns {Promise<number>} The current quantity
 */
export async function checkProductQuantity(productId) {
  // replace this with your actual Sanity client and query

  const query = `*[_type == "product" && _id == $productId][0]{
    quantity
  }`;
  const params = { productId };

  // Use write client to get fresh data for quantity checks
  const product = await sanityWriteClient.fetch(query, params);
  return product.quantity;
}

export const priceFormatter = new Intl.NumberFormat("en-US", {
  style: "decimal",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Format a price in EUR for display.
 * Expects the Sanity `price` field to already store EUR (post BGN→EUR migration).
 * @param {number} price - Price in EUR
 * @param {string} language - Current language ('en' or 'bg')
 * @param {Object} config - Configuration object with `texts.currency`
 * @returns {string} Formatted price with currency label
 */
export const formatPrice = (price, language, config) => {
  const formatted = priceFormatter.format(Number(price) || 0);
  return `${config.texts.currency[language]} ${formatted}`;
};
